import React, {useState, useEffect} from 'react'
import styled, {useTheme} from 'styled-components'
import {withStyles} from '@material-ui/core/styles'
import Tabs from '@material-ui/core/Tabs'
import Button from '@material-ui/core/Button'
import {DarkThemeProvider} from 'lib/ui/theme/ThemeProvider'
import TabPanel from 'lib/ui/ConfigPanel/TabPanel'
import ConfigDrawer, {Title} from 'lib/ui/ConfigDrawer'
import {
  SaveButton,
  Footer,
  RulesTab,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import TagsInput, {Tag} from 'lib/ui/form/TagsInput'
import {spacing} from 'lib/ui/theme'
import {
  Group,
  usePreviewAttendeeProfile,
} from 'Event/attendee-rules/AttendeeProfileProvider/PreviewAttendeeProfileProvider'
import TextField from 'lib/ui/TextField'
import ConfirmRemoveButton from 'lib/ui/Button/ConfirmRemoveButton'
import LocalizedDateTimePicker from 'lib/LocalizedDateTimePicker'
import {onChangeDate} from 'lib/dom'
import {useStaticDate} from 'lib/date-time/StaticDateProvider'
import {usePrevious} from 'lib/state'
import {usePreviewSubmissions} from 'Event/SubmissionsProvider/PreviewSubmissionsProvider'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from 'lib/ui/Menu'
import {AnswerInput} from 'Event/Dashboard/editor/views/ConfigBar/PreviewConfig/AnswerInput'
import {useEvent} from 'Event/EventProvider'
import {Panel} from 'organization/Event/DashboardConfig/ComponentConfigPanel/Panel'
import GroupInput from 'lib/ui/form/GroupInput'
import Tooltip from '@material-ui/core/Tooltip'
import AssignmentIcon from '@material-ui/icons/Assignment'
import IconButton from 'lib/ui/IconButton'
import {useIfMounted} from 'lib/dom'
import {getCopiedRules} from 'Event/attendee-rules/RulesClipboardActions'
import {getValuesMatchingRules} from 'Event/Dashboard/editor/views/ConfigBar/PreviewConfig/get-values-matching-rules'
import AttendeeSearchField from 'Event/Dashboard/editor/views/ConfigBar/PreviewConfig/AttendeeSearchField'
import {Attendee} from 'Event/attendee'
import {api} from 'lib/url'
import {useOrganization} from 'organization/OrganizationProvider'
import {Answer} from 'Event/SubmissionsProvider'
import {
  cleanAttendeeAnswers,
  prefillAnswer,
} from 'Event/Dashboard/editor/views/ConfigBar/PreviewConfig/attendee-answers'
import {uuid} from 'lib/uuid'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {useQuery, useQueryClient} from 'react-query'

const CONFIRMATION_RESET_DELAY_MS = 1500

export default function PreviewConfig(props: {
  showing: boolean
  onClose: () => void
}) {
  const {showing, onClose} = props

  const [showingPastedConfirmation, setShowingPastedConfirmation] = useState(
    false,
  )
  const ifMounted = useIfMounted()
  const theme = useTheme()
  const {event} = useEvent()
  const {client} = useOrganization()
  const queryClient = useQueryClient()

  const {
    tags: previewTags,
    setTags: setPreviewTags,
    groups: previewGroups,
    setGroups: setPreviewGroups,
    firstName: previewFirstName,
    setFirstName: setPreviewFirstName,
    lastName: previewLastName,
    setLastName: setPreviewLastName,
    email: previewEmail,
    setEmail: setPreviewEmail,
    save: saveProfile,
  } = usePreviewAttendeeProfile()

  const {date: savedDate, save: saveDate} = useSavedDate()
  const savedAttendee = useSavedAttendee()

  const previewSubmissions = usePreviewSubmissions()
  const {setAnswers: setPreviewAnswers} = previewSubmissions
  const prevShowing = usePrevious(showing)
  const staticDate = useStaticDate()

  const [tags, setTags] = useState<Tag[]>(previewTags)
  const [tagsInputKey, setTagsInputKey] = useState(uuid())
  const [groups, setGroups] = useState<Group[]>([])
  const [firstName, setFirstName] = useState(previewFirstName)
  const [lastName, setLastName] = useState(previewLastName)
  const [email, setEmail] = useState(previewEmail)
  const [date, setDate] = useState<string | null>(staticDate.date)
  const [attendee, setAttendee] = useState<Attendee | null>(
    savedAttendee.attendee,
  )

  const {
    data: attendeeAnswers = null,
    isError: failedLoadingAnswers,
  } = useQuery<Answer[], {status?: number}>({
    queryKey: ['events', event.slug, 'attendees', attendee?.id, 'submissions'],
    queryFn: () =>
      client
        .get<Answer[]>(
          api(`/events/${event.slug}/attendees/${attendee?.id}/submissions`),
        )
        .then((answers) => cleanAttendeeAnswers(answers, event.forms)),
    enabled: showing && Boolean(attendee),
    retryOnMount: false,
    retry: (failureCount, error) => error.status !== 404 && failureCount < 3,
    onError: (error) => {
      if (error.status !== 404) {
        return
      }

      setAttendee(null)
      savedAttendee.save(null)
    },
  })

  const setPreviewAttendee = (selected: Attendee | null) => {
    setAttendee(selected)

    if (!selected) {
      setFirstName('')
      setLastName('')
      setEmail('')
      setTags([])
      setTagsInputKey(uuid())
      setGroups([])
      setPreviewAnswers((current) =>
        current.map((answer) => ({...answer, id: uuid(), value: ''})),
      )
      return
    }

    setFirstName(selected.first_name ?? '')
    setLastName(selected.last_name ?? '')
    setEmail(selected.email)
    setTags(selected.tags)
    setTagsInputKey(uuid())
    setGroups(
      Object.entries(selected.groups).map(([key, value]) => ({
        key,
        value: value === null ? '' : String(value),
      })),
    )

    const queryKey = [
      'events',
      event.slug,
      'attendees',
      selected.id,
      'submissions',
    ]
    queryClient
      .prefetchQuery(queryKey, () =>
        client
          .get<Answer[]>(
            api(`/events/${event.slug}/attendees/${selected.id}/submissions`),
          )
          .then((answers) => cleanAttendeeAnswers(answers, event.forms)),
      )
      .then(() => {
        const submitted = queryClient.getQueryData<Answer[]>(queryKey)
        if (!submitted) {
          return
        }

        setPreviewAnswers((current) =>
          current.map((answer) => prefillAnswer(answer, submitted)),
        )
      })
  }

  const addGroup = () => {
    const added = [...groups, {key: '', value: ''}]
    setGroups(added)
  }

  const updateGroup = (targetIndex: number) => (updated: Group) => {
    const updatedList = groups.map((g, i) => {
      const isTarget = i === targetIndex
      if (isTarget) {
        return updated
      }

      return g
    })

    setGroups(updatedList)
  }

  const removeGroup = (targetIndex: number) => () => {
    const updated = [...groups]
    updated.splice(targetIndex, 1)
    setGroups(updated)
  }

  const copiedRules = getCopiedRules()
  const hasCopiedRules = Boolean(copiedRules?.length)

  const pasteRules = () => {
    if (!copiedRules?.length) {
      return
    }

    const values = getValuesMatchingRules({
      rules: copiedRules,
      groups: Object.fromEntries(
        groups.map((group) => [group.key, group.value]),
      ),
      tags,
      answers: previewSubmissions.answers,
      date,
      forms: event.forms,
    })

    setTags(values.tags)
    setTagsInputKey(uuid())
    setGroups(
      Object.entries(values.groups).map(([key, value]) => ({
        key,
        value: value === null ? '' : String(value),
      })),
    )
    previewSubmissions.setAnswers(values.answers)
    setDate(values.date)

    setShowingPastedConfirmation(true)
    setTimeout(
      ifMounted(() => setShowingPastedConfirmation(false)),
      CONFIRMATION_RESET_DELAY_MS,
    )
  }

  const save = () => {
    saveProfile({groups, tags, firstName, lastName, email})
    saveDate(date)
    previewSubmissions.saveAnswers()
    savedAttendee.save(attendee)
  }

  const clearAll = () => {
    setGroups([])
    setTags([])
    setTagsInputKey(uuid())
    setFirstName('')
    setLastName('')
    setEmail('')
    setDate(null)
    setAttendee(null)
    previewSubmissions.clear()
  }

  const handleCancel = () => {
    previewSubmissions.reset()
    onClose()
  }

  const handleClose = () => {
    setPreviewGroups(groups)
    setPreviewTags(tags)
    setPreviewFirstName(firstName)
    setPreviewLastName(lastName)
    setPreviewEmail(email)
    staticDate.setDate(date)
    save()
    onClose()
  }

  // Load values
  useEffect(() => {
    const onOpen = showing && !prevShowing
    if (!onOpen) {
      return
    }

    setTags(previewTags)
    setGroups(previewGroups)
    setFirstName(previewFirstName)
    setLastName(previewLastName)
    setEmail(previewEmail)
    setDate(staticDate.date)
    setAttendee(savedAttendee.attendee)
  }, [
    previewGroups,
    previewTags,
    previewFirstName,
    previewLastName,
    previewEmail,
    showing,
    prevShowing,
    staticDate,
    savedAttendee.attendee,
  ])

  // Load a saved date if one exists
  useEffect(() => {
    if (savedDate) {
      staticDate.setDate(savedDate)
    }
  }, [savedDate, staticDate])

  const pasteIconColor = hasCopiedRules
    ? theme.colors.primary
    : theme.colors.disabled

  const pasteRulesTooltip = getPasteRulesTooltip({
    showingPastedConfirmation,
    hasCopiedRules,
  })

  return (
    <ConfigDrawer open={showing} onClose={handleCancel}>
      <DarkThemeProvider>
        <Title>Preview Rules</Title>
        <StyledTabs
          value={0}
          TabIndicatorProps={{
            style: {
              display: 'none',
            },
          }}
          scrollButtons="off"
        >
          <RulesTab />
        </StyledTabs>
        <Content>
          <TabPanel value={0} index={0}>
            <Panel>
              <ClipboardContainer>
                <ClipboardLabel>Rules Clipboard</ClipboardLabel>
                <Tooltip title={pasteRulesTooltip}>
                  <IconWrap>
                    <IconButton
                      aria-label="paste rules"
                      disabled={!hasCopiedRules}
                      onClick={pasteRules}
                    >
                      <AssignmentIcon htmlColor={pasteIconColor} />
                    </IconButton>
                  </IconWrap>
                </Tooltip>
              </ClipboardContainer>
              <AttendeeSearchField
                value={attendee}
                onChange={setPreviewAttendee}
              />
              <ErrorAlert showing={failedLoadingAnswers}>
                Could not load this attendee's answers. Form responses won't be
                pre-filled.
              </ErrorAlert>
              <LocalizedDateTimePicker
                label="Date"
                value={date}
                onChange={onChangeDate(setDate)}
                fullWidth
                onClear={() => setDate(null)}
                inputProps={{
                  'aria-label': 'date input',
                }}
              />
              <TextField
                label="First Name"
                fullWidth
                variant="outlined"
                inputProps={{'aria-label': 'preview first name'}}
                value={firstName}
                onChange={setFirstName}
              />
              <TextField
                label="Last Name"
                fullWidth
                variant="outlined"
                inputProps={{'aria-label': 'preview last name'}}
                value={lastName}
                onChange={setLastName}
              />
              <TextField
                label="Email"
                fullWidth
                variant="outlined"
                inputProps={{'aria-label': 'preview email'}}
                value={email}
                onChange={setEmail}
              />
              <TagsInput
                key={tagsInputKey}
                value={tags}
                onChange={setTags}
                name="tags"
                aria-label="tags"
                label="Tags"
              />
              {groups.map((group, index) => (
                <GroupInput
                  key={index}
                  group={group}
                  onChange={updateGroup(index)}
                  remove={removeGroup(index)}
                />
              ))}
              {previewSubmissions.answers.map((answer) => (
                <AnswerInput
                  key={answer.id}
                  answer={answer}
                  attendeeAnswers={attendeeAnswers}
                />
              ))}
              <Menu
                button={({open}) => (
                  <AddButton
                    variant="outlined"
                    color="primary"
                    onClick={open}
                    fullWidth
                    size="large"
                  >
                    Add
                  </AddButton>
                )}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={addGroup}>Group</MenuItem>
                <MenuItem onClick={previewSubmissions.add}>
                  Form Response
                </MenuItem>
              </Menu>
              <ConfirmRemoveButton
                aria-label="clear all"
                onClick={clearAll}
                fullWidth
                size="large"
                variant="outlined"
              >
                Clear All
              </ConfirmRemoveButton>
            </Panel>
          </TabPanel>
        </Content>
        <Footer>
          <SaveButton onClick={handleClose} />
        </Footer>
      </DarkThemeProvider>
    </ConfigDrawer>
  )
}

function getPasteRulesTooltip(props: {
  showingPastedConfirmation: boolean
  hasCopiedRules: boolean
}) {
  if (props.showingPastedConfirmation) {
    return 'Pasted!'
  }

  if (props.hasCopiedRules) {
    return 'Paste rules'
  }

  return 'Copy rules first'
}

function useSavedAttendee() {
  const {event} = useEvent()
  const key = `__obvio_${event.id}_preview_attendee__`

  const savedJson = localStorage.getItem(key)
  const saved = savedJson ? JSON.parse(savedJson) : null
  const attendee: Attendee | null = saved?.id && saved?.email ? saved : null

  const save = (attendee: Attendee | null) => {
    if (!attendee) {
      localStorage.removeItem(key)
      return
    }

    localStorage.setItem(key, JSON.stringify(attendee))
  }

  return {attendee, save}
}

export function useSavedDate() {
  const {event} = useEvent()
  const key = `__obvio_${event.id}_preview_date__`

  const date = localStorage.getItem(key)

  const save = (date: string | null) => {
    if (!date) {
      localStorage.removeItem(key)
      return
    }

    localStorage.setItem(key, date)
  }

  return {date, save}
}

const StyledTabs = withStyles({
  flexContainer: {
    overflow: 'hidden',
  },
})(Tabs)

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const AddButton = withStyles({
  root: {
    marginTop: spacing[4],
    marginBottom: spacing[4],
  },
})(Button)

const ClipboardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[2]} 0;
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const ClipboardLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.colors.text.muted};
  margin-right: auto;
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
`
